import { createContext, useContext } from 'react'
import { CitationSource } from 'types/chat-message'
import { openSignedUrl } from 'api/uploads'
import { getFileExtension } from 'utils/chat-attachments'

/** What the right panel can show: the browser's PDF viewer, or our own table for spreadsheets. */
export type PreviewKind = 'pdf' | 'spreadsheet'

export type PreviewDocument = {
  name: string
  kind: PreviewKind
  /** The stored (bare) url: tells a late signing response which click it answers. */
  sourceUrl: string
  /** Signed url; undefined while it is fetched (the panel is already open, showing a spinner). */
  url?: string
}

/** Provided by the notebook page only: a previewable source opens in its right panel instead of a new tab. */
export const DocumentPreviewContext = createContext<((source: CitationSource, kind: PreviewKind) => void) | undefined>(
  undefined,
)

const SPREADSHEET_EXTENSIONS = ['xlsx', 'xls', 'csv']

// ponytail: extension of the stored url (or name); sniff the signed link's content-type if a tool ever emits extension-less keys.
export const getPreviewKind = (source: CitationSource): PreviewKind | undefined => {
  const extension = getFileExtension(source.url ?? '') || getFileExtension(source.name)
  if (extension === 'pdf') return 'pdf'
  if (SPREADSHEET_EXTENSIONS.includes(extension)) return 'spreadsheet'
  return undefined
}

/** Click handler for a source url: notebook + previewable file → right panel, everything else → signed link in a new tab. */
export const useOpenSource = () => {
  const preview = useContext(DocumentPreviewContext)
  return (source: CitationSource, fragment = '') => {
    const kind = getPreviewKind(source)
    return preview && kind ? preview(source, kind) : openSignedUrl(source.url, fragment)
  }
}
